import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Upload, Copy, Trash2, Image, X } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, EmptyState, DeleteConfirmModal } from '../../../components/ui'
import { formatDateTime } from '../../../utils/dateUtils'

interface MediaItem {
  id: number; file_url: string; file_name: string; file_size: number
  mime_type: string; alt_text: string | null; caption: string | null
  post_id: number | null; created_by_name: string | null; created_at: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export default function BlogMediaPage() {
  const navigate = useNavigate()
  const [media, setMedia] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => { fetchMedia() }, [])

  async function fetchMedia() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/posts/media'))
      setMedia(res.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files?.length) return
    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('file', file)
        await axios.post(buildApiUrl('/v1/posts/media'), formData)
      }
      toast('success', `${files.length} imagem${files.length > 1 ? 'ns' : ''} enviada${files.length > 1 ? 's' : ''}`)
      fetchMedia()
    } catch (err: any) { toast('error', err.response?.data?.message || err.response?.data?.error || 'Erro no upload'); console.error('Upload error:', err.response?.data || err) } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  function copyUrl(url: string) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
    } else {
      const ta = document.createElement('textarea')
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select(); document.execCommand('copy')
      document.body.removeChild(ta)
    }
    toast('success', 'URL copiada!')
  }

  async function handleDelete() {
    if (!deleteId) return
    await axios.delete(buildApiUrl(`/v1/posts/media/${deleteId}`))
    toast('success', 'Mídia excluída')
    setDeleteId(null)
    fetchMedia()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/blog/posts')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mídia</h1>
            <p className="text-gray-500 mt-1">Galeria de imagens para os posts</p>
          </div>
        </div>
        <label className={clsx('inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer',
          'bg-brand-500 text-white hover:bg-brand-600', uploading && 'opacity-70 pointer-events-none'
        )}>
          <Upload className="w-4 h-4" /> {uploading ? 'Enviando...' : 'Enviar imagens'}
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="aspect-square bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : media.length === 0 ? (
        <Card>
          <EmptyState icon={<Image className="w-7 h-7" />} title="Nenhuma mídia" description="Envie imagens para usar nos posts." />
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {media.map(m => (
            <div key={m.id} className="group relative bg-white rounded-xl border border-gray-200 overflow-hidden">
              <img src={m.file_url} alt={m.alt_text || m.file_name} className="aspect-square object-cover w-full cursor-pointer" onClick={() => setPreviewUrl(m.file_url)} />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button onClick={() => copyUrl(m.file_url)} className="h-9 w-9 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:text-brand-600">
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => setDeleteId(m.id)} className="h-9 w-9 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:text-error-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="px-3 py-2">
                <p className="text-xs text-gray-700 truncate">{m.file_name}</p>
                <p className="text-[11px] text-gray-400">{formatFileSize(m.file_size)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview */}
      {previewUrl && (
        <>
          <div className="fixed inset-0 z-[100000] bg-black/80 flex items-center justify-center" onClick={() => setPreviewUrl(null)}>
            <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={() => setPreviewUrl(null)}>
              <X className="w-8 h-8" />
            </button>
            <img src={previewUrl} alt="" className="max-w-[90vw] max-h-[90vh] rounded-lg" onClick={e => e.stopPropagation()} />
          </div>
        </>
      )}

      <DeleteConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir mídia" message="Tem certeza que deseja excluir esta imagem?" confirmLabel="Excluir" />
    </div>
  )
}
